import React, { createContext, useContext, useState, useMemo } from 'react';
import { useAuthStatus } from '@/hooks/useApi';

export type RoleView = 'preparer' | 'approver' | 'full' | 'admin' | 'auditor';

interface RoleViewContextType {
  activeView: RoleView;
  setActiveView: (view: RoleView) => void;
  availableViews: RoleView[];
  viewCounts: Record<RoleView, number>;
  updateViewCount: (view: RoleView, count: number) => void;
}

const RoleViewContext = createContext<RoleViewContextType | undefined>(undefined);

export const useRoleView = () => {
  const context = useContext(RoleViewContext);
  if (!context) {
    throw new Error('useRoleView must be used within a RoleViewProvider');
  }
  return context;
};

export const RoleViewProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { data: authStatus } = useAuthStatus();
  const [activeView, setActiveViewState] = useState<RoleView>(() => {
    // Try to get saved preference first
    const savedView = localStorage.getItem('preferredRoleView') as RoleView;
    return savedView || 'full';
  });
  const [viewCounts, setViewCounts] = useState<Record<RoleView, number>>({
    preparer: 0,
    approver: 0,
    full: 0,
    admin: 0,
    auditor: 0,
  });

  // Determine available views based on user roles
  const availableViews = useMemo(() => {
    const views: RoleView[] = [];
    
    if (authStatus?.user?.roles) {
      const roles = authStatus.user.roles;
      
      if (roles.includes('preparer')) {
        views.push('preparer');
      }
      if (roles.includes('approver')) {
        views.push('approver');
      }
      if (roles.includes('admin')) {
        views.push('admin');
        views.push('full'); // Admins always get full view
      }
      if (roles.includes('auditor')) {
        views.push('auditor');
      }
      
      // If user has multiple roles, add full view option
      if (views.length > 1 && !views.includes('full')) {
        views.push('full');
      }
      
      // Senior approvers get all views
      if (roles.includes('senior_approver')) {
        if (!views.includes('preparer')) views.push('preparer');
        if (!views.includes('approver')) views.push('approver');
        if (!views.includes('full')) views.push('full');
      }
    }
    
    return views;
  }, [authStatus?.user?.roles]);

  // Remove the problematic useEffect to fix infinite loop issue
  // Will handle view initialization differently if needed

  const setActiveView = (view: RoleView) => {
    if (availableViews.includes(view)) {
      setActiveViewState(view);
      localStorage.setItem('preferredRoleView', view);
    }
  };

  const updateViewCount = React.useCallback((view: RoleView, count: number) => {
    setViewCounts(prev => ({
      ...prev,
      [view]: count
    }));
  }, []);

  return (
    <RoleViewContext.Provider
      value={{
        activeView,
        setActiveView,
        availableViews,
        viewCounts,
        updateViewCount,
      }}
    >
      {children}
    </RoleViewContext.Provider>
  );
};